/** Default until platform settings are loaded from catalog-service */
export const DEFAULT_PLATFORM_TIMEZONE = 'Asia/Kolkata';

let platformTimezone = DEFAULT_PLATFORM_TIMEZONE;

export function getPlatformTimezone(): string {
  return platformTimezone;
}

export function setPlatformTimezone(timezone: string): void {
  if (timezone?.trim()) {
    platformTimezone = timezone.trim();
  }
}

/**
 * Parse API date values.
 *
 * Backends often serialize LocalDateTime as ISO without offset (e.g. "2026-07-14T10:57:00").
 * Azure / server clocks are UTC, but `new Date("...without Z")` is treated as *local* time
 * in the browser. For an IST user that makes a brand-new comment look ~5 hours old.
 *
 * Rule: if the string has no timezone, treat it as UTC.
 */
export function parseApiDate(value?: string | Date | null): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  // Already has an explicit timezone / offset
  if (/[zZ]$/.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw)) {
    const dated = new Date(raw);
    return Number.isNaN(dated.getTime()) ? null : dated;
  }

  // "2026-07-14 10:57:00" -> ISO-ish
  let normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  // Truncate fractional seconds noise if present without timezone
  normalized = normalized.replace(/(\.\d{1,9})$/, '');

  const asUtc = new Date(`${normalized}Z`);
  return Number.isNaN(asUtc.getTime()) ? null : asUtc;
}

function toDate(value?: string | Date | null): Date | null {
  return parseApiDate(value);
}

/** Format a UTC/ISO timestamp as a date in the platform timezone */
export function formatPlatformDate(
  value?: string | Date | null,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = toDate(value);
  if (!date) return '';

  return new Intl.DateTimeFormat('en-IN', {
    timeZone: platformTimezone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  }).format(date);
}

/** Format a UTC/ISO timestamp as date + time in the platform timezone */
export function formatPlatformDateTime(
  value?: string | Date | null,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = toDate(value);
  if (!date) return '';

  return new Intl.DateTimeFormat('en-IN', {
    timeZone: platformTimezone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  }).format(date);
}

/** Month + year only (work experience, education) */
export function formatPlatformMonthYear(value?: string | Date | null): string {
  const date = toDate(value);
  if (!date) return '';

  return new Intl.DateTimeFormat('en-IN', {
    timeZone: platformTimezone,
    month: 'short',
    year: 'numeric',
  }).format(date);
}

/** Relative time ("Just now", "5 minutes ago") — independent of display timezone. */
export function formatRelativeTime(value?: string | Date | null): string {
  const date = toDate(value);
  if (!date) return '';

  const diffMs = Date.now() - date.getTime();
  const diffInMinutes = Math.floor(diffMs / (1000 * 60));
  const diffInHours = diffMs / (1000 * 60 * 60);

  if (diffInMinutes <= 1) return 'Just now';
  if (diffInHours < 1) return `${diffInMinutes} minutes ago`;
  if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  }
  if (diffInHours < 168) {
    const days = Math.floor(diffInHours / 24);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }

  return formatPlatformDate(date, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
}
